import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { updateCartInProfile } from '../../util/cartProfile';
import { cartTransactionLineItems } from '../../util/api';
import { addToCart, removeFromCart, setDeliveryMethod, clearSellerCart } from '../../util/cartHelpers';
import { getSellerCartListingIds } from '../../config/configCart';
import * as log from '../../util/log';
import { createImageVariantConfig } from '../../util/sdkLoader';

// The listing image config (config.layout.listingImage), remembered from the
// last fetch that was given it (cart page, drawer, add-to-cart). Checkout shows
// the cart's first book with the listing-card image size, so every cart fetch
// must ask for that size too, or
// later refetches (adding a book, the topbar drawer) would replace the images
// with ones that lack it and checkout would show "NO IMAGE".
let listingImageConfig = null;

const listingCardImageParams = () => {
  if (!listingImageConfig) {
    return {};
  }
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    ...createImageVariantConfig(variantPrefix, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const listingCardFieldNames = () => {
  if (!listingImageConfig) {
    return [];
  }
  const { variantPrefix = 'listing-card' } = listingImageConfig;
  return [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CartPage/SET_INITIAL_VALUES';

export const FETCH_CART_LISTINGS_REQUEST = 'app/CartPage/FETCH_CART_LISTINGS_REQUEST';
export const FETCH_CART_LISTINGS_SUCCESS = 'app/CartPage/FETCH_CART_LISTINGS_SUCCESS';
export const FETCH_CART_LISTINGS_ERROR = 'app/CartPage/FETCH_CART_LISTINGS_ERROR';

export const TOGGLE_CART_REQUEST = 'app/CartPage/TOGGLE_CART_REQUEST';
export const TOGGLE_CART_SUCCESS = 'app/CartPage/TOGGLE_CART_SUCCESS';
export const TOGGLE_CART_ERROR = 'app/CartPage/TOGGLE_CART_ERROR';

export const SET_CART_DRAWER_OPEN = 'app/CartPage/SET_CART_DRAWER_OPEN';

export const FETCH_LINE_ITEMS_REQUEST = 'app/CartPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/CartPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/CartPage/FETCH_LINE_ITEMS_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchCartListingsInProgress: false,
  fetchCartListingsError: null,
  cartListingIds: [],
  // Cart listing ids asked for in the last fetch, and those that did not come
  // back: closed or deleted since they were added. Kept apart from "not loaded
  // yet", so a book still loading is never mistaken for one that is gone.
  requestedCartListingIds: [],
  unavailableCartListingIds: [],

  toggleCartInProgress: false,
  toggleCartError: null,

  // Topbar cart drawer, opened automatically when an item is added to the cart.
  cartDrawerOpen: false,

  // keyed by authorId
  sellerLineItems: {},
  fetchLineItemsInProgress: {},
  fetchLineItemsError: {},
};

const cartPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SET_CART_DRAWER_OPEN:
      return { ...state, cartDrawerOpen: payload };

    case FETCH_CART_LISTINGS_REQUEST:
      return {
        ...state,
        fetchCartListingsInProgress: true,
        fetchCartListingsError: null,
        requestedCartListingIds: payload || [],
      };
    case FETCH_CART_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchCartListingsInProgress: false,
        cartListingIds: payload,
        unavailableCartListingIds: state.requestedCartListingIds.filter(
          id => !payload.some(listingId => listingId.uuid === id)
        ),
      };
    case FETCH_CART_LISTINGS_ERROR:
      return { ...state, fetchCartListingsInProgress: false, fetchCartListingsError: payload };

    case TOGGLE_CART_REQUEST:
      return { ...state, toggleCartInProgress: true, toggleCartError: null };
    case TOGGLE_CART_SUCCESS:
      return { ...state, toggleCartInProgress: false };
    case TOGGLE_CART_ERROR:
      return { ...state, toggleCartInProgress: false, toggleCartError: payload };

    case FETCH_LINE_ITEMS_REQUEST:
      return {
        ...state,
        fetchLineItemsInProgress: { ...state.fetchLineItemsInProgress, [payload]: true },
        fetchLineItemsError: { ...state.fetchLineItemsError, [payload]: null },
      };
    case FETCH_LINE_ITEMS_SUCCESS:
      return {
        ...state,
        fetchLineItemsInProgress: {
          ...state.fetchLineItemsInProgress,
          [payload.authorId]: false,
        },
        sellerLineItems: { ...state.sellerLineItems, [payload.authorId]: payload.lineItems },
      };
    case FETCH_LINE_ITEMS_ERROR:
      return {
        ...state,
        fetchLineItemsInProgress: {
          ...state.fetchLineItemsInProgress,
          [payload.authorId]: false,
        },
        fetchLineItemsError: {
          ...state.fetchLineItemsError,
          [payload.authorId]: payload.error,
        },
      };

    default:
      return state;
  }
};

export default cartPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: initialValues,
});

const fetchCartListingsRequest = listingIds => ({
  type: FETCH_CART_LISTINGS_REQUEST,
  payload: listingIds,
});
const fetchCartListingsSuccess = listingIds => ({
  type: FETCH_CART_LISTINGS_SUCCESS,
  payload: listingIds,
});
const fetchCartListingsError = e => ({
  type: FETCH_CART_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const setCartDrawerOpen = isOpen => ({ type: SET_CART_DRAWER_OPEN, payload: isOpen });

const toggleCartRequest = () => ({ type: TOGGLE_CART_REQUEST });
const toggleCartSuccess = () => ({ type: TOGGLE_CART_SUCCESS });
const toggleCartError = e => ({ type: TOGGLE_CART_ERROR, error: true, payload: e });

const fetchLineItemsRequest = authorId => ({
  type: FETCH_LINE_ITEMS_REQUEST,
  payload: authorId,
});
const fetchLineItemsSuccess = (authorId, lineItems) => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: { authorId, lineItems },
});
const fetchLineItemsError = (authorId, error) => ({
  type: FETCH_LINE_ITEMS_ERROR,
  payload: { authorId, error },
});

// ================ Thunks ================ //

// Cart persistence (updateProfile + currentUserShowSuccess) lives in the shared
// `util/cartProfile` helper so ListingPage and CartPage stay in sync.

export const toggleCart = (listingId, authorId, increment) => (dispatch, getState, sdk) => {
  dispatch(toggleCartRequest());

  const currentUser = getState().user.currentUser;
  if (!currentUser) {
    return dispatch(toggleCartError(new Error('User not signed in')));
  }

  const currentCart = currentUser.attributes?.profile?.privateData?.cart || {};

  let newCart;
  try {
    newCart = addToCart(currentCart, authorId, listingId, increment);
  } catch (e) {
    dispatch(toggleCartError(storableError(e)));
    return Promise.reject(e);
  }

  return updateCartInProfile(sdk, dispatch, newCart)
    .then(() => {
      // No drawer here: the buyer is already looking at the cart. Adding from a
      // book's page opens it (ListingPage.duck's toggleCart).
      dispatch(toggleCartSuccess());
    })
    .catch(e => {
      dispatch(toggleCartError(storableError(e)));
    });
};

export const updateDeliveryMethod = (authorId, method) => (dispatch, getState, sdk) => {
  dispatch(toggleCartRequest());

  const currentUser = getState().user.currentUser;
  if (!currentUser) {
    return dispatch(toggleCartError(new Error('User not signed in')));
  }

  const currentCart = currentUser.attributes?.profile?.privateData?.cart || {};
  const newCart = setDeliveryMethod(currentCart, authorId, method);

  return updateCartInProfile(sdk, dispatch, newCart)
    .then(() => {
      dispatch(toggleCartSuccess());
    })
    .catch(e => {
      dispatch(toggleCartError(storableError(e)));
    });
};

export const removeSellerCart = authorId => (dispatch, getState, sdk) => {
  dispatch(toggleCartRequest());

  const currentUser = getState().user.currentUser;
  if (!currentUser) {
    return dispatch(toggleCartError(new Error('User not signed in')));
  }

  const currentCart = currentUser.attributes?.profile?.privateData?.cart || {};
  const newCart = clearSellerCart(currentCart, authorId);

  return updateCartInProfile(sdk, dispatch, newCart)
    .then(() => {
      dispatch(toggleCartSuccess());
    })
    .catch(e => {
      dispatch(toggleCartError(storableError(e)));
    });
};

export const removeCartItem = (listingId, authorId) => (dispatch, getState, sdk) => {
  dispatch(toggleCartRequest());

  const currentUser = getState().user.currentUser;
  if (!currentUser) {
    return dispatch(toggleCartError(new Error('User not signed in')));
  }

  const currentCart = currentUser.attributes?.profile?.privateData?.cart || {};
  const newCart = removeFromCart(currentCart, authorId, listingId);

  return updateCartInProfile(sdk, dispatch, newCart)
    .then(() => {
      dispatch(toggleCartSuccess());
    })
    .catch(e => {
      dispatch(toggleCartError(storableError(e)));
    });
};

export const fetchCartLineItemsForSeller = (authorId, sellerCart, voucherCode) => dispatch => {
  dispatch(fetchLineItemsRequest(authorId));

  const cart = {};
  const listingIds = getSellerCartListingIds(sellerCart);
  listingIds.forEach(id => {
    cart[id] = sellerCart[id];
  });
  cart.deliveryMethod = sellerCart.deliveryMethod;

  return cartTransactionLineItems({ cart, voucherCode })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(authorId, lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(authorId, storableError(e)));
      log.error(e, 'fetching-cart-line-items-failed', { authorId });
    });
};

// Fetch the listings currently in the user's cart into marketplaceData, and
// record their ids. Used by CartPage's loadData and by the topbar mini-cart.
export const fetchCartListings = config => (dispatch, getState, sdk) => {
  if (config?.layout?.listingImage) {
    listingImageConfig = config.layout.listingImage;
  }
  const currentUser = getState().user.currentUser;
  const cart = currentUser?.attributes?.profile?.privateData?.cart || {};
  const allListingIds = [];

  Object.keys(cart).forEach(authorId => {
    const sellerCart = cart[authorId];
    const listingIds = getSellerCartListingIds(sellerCart);
    allListingIds.push(...listingIds);
  });

  if (allListingIds.length === 0) {
    dispatch(fetchCartListingsRequest([]));
    dispatch(fetchCartListingsSuccess([]));
    return Promise.resolve();
  }

  dispatch(fetchCartListingsRequest(allListingIds));

  return sdk.listings
    .query({
      ids: allListingIds,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': [
        'variants.scaled-small',
        'variants.square-small',
        'variants.square-small2x',
        ...listingCardFieldNames(),
      ],
      ...listingCardImageParams(),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const ids = response.data.data.map(l => l.id);
      dispatch(fetchCartListingsSuccess(ids));
    })
    .catch(e => {
      dispatch(fetchCartListingsError(storableError(e)));
    });
};

export const loadData = (params, search, config) => dispatch => {
  dispatch(setInitialValues({ sellerLineItems: {}, fetchLineItemsInProgress: {} }));
  return dispatch(fetchCartListings(config));
};
